/**
 * CR221: Preset Picker Bar
 * 
 * Compact top bar for selecting and managing layout presets.
 * Replaces the left sidebar column to reclaim horizontal space.
 */

import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ChevronDown,
  Plus,
  Copy,
  Archive,
  Settings2,
  Save,
  AlertCircle,
  CheckCircle2,
  Clock,
  RotateCcw, // CR290D: Discard icon
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import type { LayoutPreset } from "@/lib/layout-studio/types";

interface PresetPickerBarProps {
  presets: LayoutPreset[];
  selectedPresetId: string | null;
  stagingPresetId: string | null;
  publishPresetId: string | null;
  hasUnsavedChanges: boolean;
  saving: boolean;
  lastSavedAt?: Date | null;
  onSelectPreset: (presetId: string) => void;
  onCreateNew: () => void;
  onClone: () => void;
  onArchive: () => void;
  onSave: () => void;
  onDiscard: () => void; // CR290D: Revert to last saved state
  onManagePresets: () => void;
}

export function PresetPickerBar({
  presets,
  selectedPresetId,
  stagingPresetId,
  publishPresetId,
  hasUnsavedChanges,
  saving,
  lastSavedAt,
  onSelectPreset,
  onCreateNew,
  onClone,
  onArchive,
  onSave,
  onDiscard,
  onManagePresets,
}: PresetPickerBarProps) {
  const selectedPreset = presets.find(p => p.id === selectedPresetId);
  
  // Determine status pills for a preset
  const getPresetStatusPills = (preset: LayoutPreset) => {
    const pills: { label: string; variant: "default" | "secondary" | "outline" }[] = [];
    if (publishPresetId === preset.id) {
      pills.push({ label: "LIVE", variant: "default" });
    }
    if (stagingPresetId === preset.id) {
      pills.push({ label: "STAGING", variant: "secondary" });
    }
    return pills;
  };

  return (
    <div className="flex items-center justify-between gap-4 px-4 py-2 border-b bg-card">
      {/* Left: Preset Selector */}
      <div className="flex items-center gap-3">
        <Select
          value={selectedPresetId || ""}
          onValueChange={(value) => value && onSelectPreset(value)}
        >
          <SelectTrigger className="w-[280px] h-9">
            <SelectValue placeholder="Select a preset...">
              {selectedPreset && (
                <div className="flex items-center gap-2">
                  <span className="truncate">{selectedPreset.name}</span>
                  {getPresetStatusPills(selectedPreset).map((pill, i) => (
                    <Badge key={i} variant={pill.variant} className="text-[9px] px-1 py-0">
                      {pill.label}
                    </Badge>
                  ))}
                </div>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="bg-popover border shadow-lg z-50">
            {presets.map((preset) => {
              const pills = getPresetStatusPills(preset);
              return (
                <SelectItem key={preset.id} value={preset.id}>
                  <div className="flex items-center gap-2">
                    <span className="truncate">{preset.name}</span>
                    {pills.map((pill, i) => (
                      <Badge key={i} variant={pill.variant} className="text-[9px] px-1 py-0">
                        {pill.label}
                      </Badge>
                    ))}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {/* Actions Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-9 px-2">
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="bg-popover border shadow-lg z-50">
            <DropdownMenuItem onClick={onCreateNew}>
              <Plus className="h-4 w-4 mr-2" />
              New Preset
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onClone} disabled={!selectedPresetId}>
              <Copy className="h-4 w-4 mr-2" />
              Clone
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onArchive} disabled={!selectedPresetId}>
              <Archive className="h-4 w-4 mr-2" />
              Archive
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onManagePresets}>
              <Settings2 className="h-4 w-4 mr-2" />
              Manage All Presets
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Right: Status + Save */}
      <div className="flex items-center gap-3">
        {/* Unsaved indicator */}
        {hasUnsavedChanges ? (
          <div className="flex items-center gap-1.5 text-warning">
            <AlertCircle className="h-4 w-4" />
            <span className="text-xs font-medium">Unsaved changes</span>
          </div>
        ) : lastSavedAt ? (
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-primary" />
            <span className="text-xs">
              Saved {formatDistanceToNow(lastSavedAt, { addSuffix: true })}
            </span>
          </div>
        ) : selectedPreset ? (
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span className="text-xs">
              Updated {formatDistanceToNow(new Date(selectedPreset.updated_at), { addSuffix: true })}
            </span>
          </div>
        ) : null}

        {/* CR290D: Discard Button */}
        {hasUnsavedChanges && (
          <Button
            size="sm"
            variant="ghost"
            className="h-9"
            onClick={onDiscard}
            disabled={saving}
          >
            <RotateCcw className="h-4 w-4 mr-1.5" />
            Discard
          </Button>
        )}

        {/* Save Button */}
        <Button
          size="sm"
          className="h-9"
          onClick={onSave}
          disabled={!hasUnsavedChanges || saving || !selectedPresetId}
          variant={hasUnsavedChanges ? "default" : "outline"}
        >
          <Save className="h-4 w-4 mr-1.5" />
          {saving ? "Saving..." : "Save Draft"}
        </Button>
      </div>
    </div>
  );
}
